import React, { useState, useEffect } from 'react';
import AdvisorCard from './AdvisorCard';
import CompatibilityMatrix from './CompatibilityMatrix';
import MatchingEngine from './MatchingEngine';
import LoadingState from '../common/LoadingState';

const AdvisorCompatibilityTab = () => {
  const [activeTab, setActiveTab] = useState('matching');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterCriteria, setFilterCriteria] = useState({
    minCompatibility: 75,
    clientType: 'all',
    investmentStyle: 'all'
  });
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [existingMatches, setExistingMatches] = useState([]);
  const [advisorMatrix, setAdvisorMatrix] = useState([]);

  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setIsLoading(false);
      // Load mock data here
      setExistingMatches(mockExistingMatches);
      setAdvisorMatrix(mockAdvisorMatrix);
      // Set default selected match and advisor
      setSelectedMatch(mockExistingMatches[0]);
      setSelectedAdvisor(mockAdvisorMatrix[0]);
    }, 1000);
    
    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Function to handle match selection
  const handleMatchSelect = (match) => {
    setSelectedMatch(match);
  };

  // Function to handle advisor selection for matrix view
  const handleAdvisorSelect = (advisor) => {
    setSelectedAdvisor(advisor);
  };

  // Function to handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterCriteria(prev => ({
      ...prev,
      [name]: name === 'minCompatibility' ? parseInt(value) : value
    }));
  };

  if (isLoading) {
    return <LoadingState message="Loading compatibility data..." />;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
      <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">SPC-Advisor Compatibility Matching</h2>
      <p className="text-sm md:text-base text-gray-600 mb-6">
        Pairs Strategic Priority Clients with the most suitable advisors based on previous client handling experience and predictive analytics.
      </p>

      {/* Tab navigation */}
      <div className="flex border-b mb-6">
        <button 
          className={`px-4 py-2 font-medium text-sm md:text-base ${activeTab === 'matching' ? 'text-primaryDark border-b-2 border-blue-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('matching')}
        >
          AI Matching Engine
        </button>
        <button 
          className={`px-4 py-2 font-medium text-sm md:text-base ${activeTab === 'matrix' ? 'text-primaryDark border-b-2 border-blue-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('matrix')}
        >
          Advisor Experience Matrix
        </button>
      </div>

      {/* AI Matching Engine Tab */}
      {activeTab === 'matching' && (
        <div>
          {/* AI Matching Engine Description */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <div className="bg-blue-100 rounded-full p-2 mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primaryDark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-primaryDark mb-1">AI-Powered Advisor-Client Matching</h3>
                <p className="text-sm text-primaryDark">
                  Our proprietary AI algorithm analyzes over 50 data points including advisor expertise, client preferences, 
                  communication styles, and historical performance to create optimal pairings with the highest probability of success.
                </p>
              </div>
            </div>
          </div>

          {/* Filtering options */}
          <div className="bg-gray-50 border rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-800 mb-3">Filter Matching Criteria</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Compatibility</label>
                <div className="flex items-center">
                  <input
                    type="range"
                    name="minCompatibility"
                    min="50"
                    max="100"
                    value={filterCriteria.minCompatibility}
                    onChange={handleFilterChange}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="ml-2 text-sm font-medium">{filterCriteria.minCompatibility}%</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Type</label>
                <select
                  name="clientType"
                  value={filterCriteria.clientType}
                  onChange={handleFilterChange}
                  className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Client Types</option>
                  <option value="retiree">Retirees</option>
                  <option value="executive">Executives</option>
                  <option value="entrepreneur">Entrepreneurs</option>
                  <option value="professional">Professionals</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Investment Style</label>
                <select
                  name="investmentStyle"
                  value={filterCriteria.investmentStyle}
                  onChange={handleFilterChange}
                  className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Styles</option>
                  <option value="conservative">Conservative</option>
                  <option value="moderate">Moderate</option>
                  <option value="aggressive">Aggressive</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="w-full lg:w-1/2">
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 border-b p-3">
                  <h3 className="font-semibold text-gray-700">ESTABLISHED CLIENT-ADVISOR MATCHES</h3>
                </div>

                {existingMatches.map((match) => (
                  <div 
                    key={match.id} 
                    className={`flex items-center p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${selectedMatch?.id === match.id ? 'bg-blue-50' : ''}`}
                    onClick={() => handleMatchSelect(match)}
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden mr-3">
                      <img 
                        src={match.client.image} 
                        alt={match.client.name} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-grow">
                      <h4 className="font-medium text-gray-800">{match.client.name}</h4>
                      <p className="text-gray-500 text-sm">{match.client.location} • {match.client.assets}</p>
                    </div>
                    <div className="flex items-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-3 ${match.compatibilityScore >= 90 ? 'bg-green-100' : match.compatibilityScore >= 80 ? 'bg-blue-100' : 'bg-yellow-100'}`}>
                        <span className="font-bold text-sm">{match.compatibilityScore}</span>
                      </div>
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img 
                          src={match.advisor.image} 
                          alt={match.advisor.name} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Details panel for selected match */}
            <div className="w-full lg:w-1/2">
              {selectedMatch && <MatchingEngine selectedMatch={selectedMatch} />}
            </div>
          </div>
        </div>
      )}

      {/* Advisor Experience Matrix Tab */}
      {activeTab === 'matrix' && (
        <div>
          <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <div className="bg-purple-100 rounded-full p-2 mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-purple-800 mb-1">Advisor Experience Ranking Matrix</h3>
                <p className="text-sm text-purple-700">
                  Comprehensive tracking of advisor expertise across client personas, industries, investment types, and financial goals.
                  This data powers our AI matching algorithm and helps identify the most suitable advisors for each client profile.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="w-full lg:w-1/3">
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 border-b p-3">
                  <h3 className="font-semibold text-gray-700">ADVISOR ROSTER</h3>
                </div>

                {advisorMatrix.map((advisor) => (
                  <AdvisorCard 
                    key={advisor.id}
                    advisor={advisor}
                    isSelected={selectedAdvisor?.id === advisor.id}
                    onSelect={handleAdvisorSelect}
                  />
                ))}
              </div>
            </div>

            {/* Advisor Experience Matrix */}
            <div className="w-full lg:w-2/3">
              {selectedAdvisor && <CompatibilityMatrix selectedAdvisor={selectedAdvisor} />}
            </div>
          </div>
        </div>
      )}

      {/* Analytics summary section */}
      <div className="mt-8 border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">AI Matching Analytics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-primaryDark text-sm font-medium mb-1">Average Compatibility</div>
            <div className="text-2xl font-bold text-gray-800">89.5%</div>
            <div className="text-xs text-gray-500 mt-1">+2.3% from last quarter</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-green-600 text-sm font-medium mb-1">Client Satisfaction</div>
            <div className="text-2xl font-bold text-gray-800">4.7/5.0</div>
            <div className="text-xs text-gray-500 mt-1">+0.2 from last quarter</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
             <div className="text-purple-600 text-sm font-medium mb-1">Client Retention</div>
            <div className="text-2xl font-bold text-gray-800">97.2%</div>
            <div className="text-xs text-gray-500 mt-1">+1.5% from last quarter</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-4">
            <div className="text-amber-600 text-sm font-medium mb-1">Portfolio Growth</div>
            <div className="text-2xl font-bold text-gray-800">14.3%</div>
            <div className="text-xs text-gray-500 mt-1">+3.7% from last quarter</div>
          </div>
        </div>
      </div>

      {/* AI Methodology information */}
      <div className="mt-8 bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <h4 className="font-medium text-gray-700 mb-2">About the AI Matching Algorithm</h4>
        <p className="mb-2">
          Our proprietary AI matching algorithm analyzes over 50 data points including advisor expertise, 
          client preferences, communication styles, investment philosophies, and historical performance 
          to create optimal client-advisor pairings.
        </p>
        <p>
          The algorithm uses embeddings trained on historical success data (AUM growth, retention, and sentiment match)
          to rank advisors based on their compatibility with specific client profiles and behavioral characteristics.
        </p>
      </div>
    </div>
  );
};

// Mock data for existing client-advisor matches
const mockExistingMatches = [
  {
    id: 1,
    client: {
      name: "Emma Roberts",
      location: "New York",
      image: "https://randomuser.me/api/portraits/women/44.jpg",
      assets: "$2.4M",
      clientSince: "2018",
      preferences: ["Conservative approach", "Regular updates", "Tech-savvy"]
    },
    advisor: {
      name: "Michael Harris",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
      experience: "12 years",
      clients: 24,
      avgSatisfaction: 4.8
    },
    compatibilityScore: 92,
    expertiseAreas: [
      { name: "Retirement Planning", score: 95 },
      { name: "Estate Planning", score: 90 },
      { name: "Tax Optimization", score: 85 }
    ],
    relationshipMetrics: {
      meetings: [4, 6, 5, 7, 8, 6],
      satisfaction: [4.2, 4.5, 4.6, 4.7, 4.8, 4.9],
      retention: 98,
      growth: 12.5
    }
  },
  // Add more mock matches as needed
];

// Mock data for advisor matrix
const mockAdvisorMatrix = [
  {
    id: 1,
    name: "Michael Harris",
    image: "https://randomuser.me/api/portraits/men/32.jpg",
    experience: "12 years",
    clientTypes: [
      { type: "Retirees", score: 95 },
      { type: "Business Owners", score: 82 },
      { type: "Tech Executives", score: 78 },
      { type: "Medical Professionals", score: 88 }
    ],
    industries: [
      { name: "Healthcare", score: 90 },
      { name: "Technology", score: 75 },
      { name: "Real Estate", score: 85 },
      { name: "Finance", score: 92 }
    ],
    investmentTypes: [
      { name: "Fixed Income", score: 94 },
      { name: "Equities", score: 88 },
      { name: "Alternative Investments", score: 76 },
      { name: "ESG", score: 82 }
    ],
    clientGoals: [
      { name: "Retirement Planning", score: 96 },
      { name: "Estate Planning", score: 90 },
      { name: "Tax Optimization", score: 88 },
      { name: "Education Funding", score: 85 }
    ]
  },
  // Add more mock advisors as needed
];

export default AdvisorCompatibilityTab;